/**
 * Tauri bridge for the desktop meeting recorder.
 * Same invoke/listen idiom as tauri.ts — all functions are isTauri()-guarded
 * and resolve to safe defaults in the browser.
 */

import { env } from '@/utils/env'

import { useOrganizationStore } from '@/store/organization.store'
import { isLiveCaptionsPreferred } from '@/store/recording.store'
import { desktopSecureStorage, isTauri } from './tauri'

// ===== Types mirrored from src-tauri/src/recording/types.rs =====

export type RecordingPermission = 'granted' | 'denied' | 'undetermined'

export interface RecordingSupport {
  supported: boolean
  reason?: 'os_version' | 'platform' | string | null
  systemAudio: boolean
  /**
   * How system audio is captured: 'tap' (CoreAudio process tap, macOS
   * 14.4+, one-click permission), 'screen' (ScreenCaptureKit, Screen
   * Recording grant + restart), or 'none'.
   */
  systemAudioMode: 'tap' | 'screen' | 'none' | string
}

export interface RecordingPermissions {
  microphone: RecordingPermission | string
  screenRecording: RecordingPermission | string
}

export interface RecordingStateDto {
  state: 'idle' | 'recording' | 'paused' | 'finalizing' | string
  recordingId?: string | null
  startedAt?: string | null
  durationMs: number
  moduleType?: string | null
  moduleId?: string | null
  eventId?: string | null
}

export interface RecordingInfo {
  id: string
  state: string
  startedAt: string
  durationMs: number
  fileSizeBytes: number
  eventId?: string | null
  moduleType?: string | null
  moduleId?: string | null
  apiRecordingId?: string | null
}

/** Calendar context forwarded to the API as `metadata.event` — the server
 *  uses attendees as speaker-name hints for the AI summarizer. */
export interface RecordingEventContext {
  externalEventId?: string
  title?: string
  provider?: string
  attendees?: { name?: string; email?: string }[]
}

export interface RecordingStartOptions {
  eventId?: string
  moduleType?: string
  moduleId?: string
  micDeviceId?: string
  eventContext?: RecordingEventContext
  /** Summary template ref (builtin key | 'custom:<uuid>') */
  summaryTemplate?: string
  /** On-device live captions (macOS desktop; needs the caption model) */
  liveCaptions?: boolean
}

export interface MeetingDetection {
  conferencingApp: string | null
  micInUse: boolean
}

export interface CaptionModelStatus {
  supported: boolean
  state: 'ready' | 'absent' | 'downloading' | string
}

export interface CaptionPayload {
  text: string
  startMs: number
  endMs: number
  /** Channel attribution: 'you' (your mic) or 'others' (system audio) */
  speaker?: 'you' | 'others'
}

export interface MicDevice {
  id: string
  name: string
  isDefault: boolean
}

export interface RecordingLevelPayload {
  mic: number
  system: number
}

export interface RecordingStatePayload {
  state: string
  recordingId?: string | null
  startedAt?: string | null
  durationMs: number
  pausedMs?: number
}

export interface RecordingUploadProgressPayload {
  recordingId: string
  uploadedBytes: number
  totalBytes: number
}

export interface RecordingErrorPayload {
  code: string
  message: string
}

// ===== Commands =====

async function invokeTauri<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import('@tauri-apps/api/core')
  return invoke<T>(command, args)
}

export async function getRecordingSupport(): Promise<RecordingSupport> {
  if (!isTauri()) {
    return { supported: false, reason: 'platform', systemAudio: false, systemAudioMode: 'none' }
  }
  try {
    return await invokeTauri<RecordingSupport>('recording_get_support')
  } catch {
    return { supported: false, reason: 'platform', systemAudio: false, systemAudioMode: 'none' }
  }
}

export async function checkRecordingPermissions(): Promise<RecordingPermissions> {
  if (!isTauri()) {
    return { microphone: 'denied', screenRecording: 'denied' }
  }
  return invokeTauri<RecordingPermissions>('recording_check_permissions')
}

export async function requestMicPermission(): Promise<string> {
  if (!isTauri()) return 'denied'
  return invokeTauri<string>('recording_request_mic_permission')
}

export async function requestScreenRecordingPermission(): Promise<string> {
  if (!isTauri()) return 'denied'
  return invokeTauri<string>('recording_request_screen_permission')
}

/**
 * One-click system-audio permission via CoreAudio process taps (macOS
 * 14.4+). Returns 'granted' | 'denied' | 'unsupported'. No app restart
 * needed, and macOS auto-adds the app to the "System Audio Recording Only"
 * Settings list.
 */
export async function requestSystemAudioPermission(): Promise<string> {
  if (!isTauri()) return 'unsupported'
  return invokeTauri<string>('recording_request_system_audio')
}

/**
 * Real system-audio authorization status, read WITHOUT prompting (via the
 * TCC preflight SPI). Returns 'granted' | 'denied' | 'undetermined' — used
 * to reflect the true grant in the UI instead of guessing.
 */
export async function getSystemAudioStatus(): Promise<string> {
  if (!isTauri()) return 'undetermined'
  try {
    return await invokeTauri<string>('recording_system_audio_status')
  } catch {
    return 'undetermined'
  }
}

export async function openRecordingPrivacySettings(
  pane: 'microphone' | 'screenRecording' | 'systemAudio',
): Promise<void> {
  if (!isTauri()) return
  await invokeTauri('recording_open_privacy_settings', { pane })
}

export async function startRecording(
  options: RecordingStartOptions = {},
): Promise<RecordingInfo> {
  return invokeTauri<RecordingInfo>('recording_start', { options })
}

/**
 * One-shot ad-hoc meeting probe: a conferencing app (Zoom/Teams/Webex) is
 * running AND some process holds the default mic open. Polled while idle to
 * offer recording calls that aren't on the calendar.
 */
export async function detectActiveMeeting(): Promise<MeetingDetection | null> {
  if (!isTauri()) return null
  try {
    return await invokeTauri<MeetingDetection>('recording_detect_meeting')
  } catch {
    return null
  }
}

export async function updateRecordingLink(
  moduleType: string | null,
  moduleId: string | null,
): Promise<void> {
  await invokeTauri('recording_update_link', { moduleType, moduleId })
}

export async function getCaptionModelStatus(): Promise<CaptionModelStatus> {
  if (!isTauri()) return { supported: false, state: 'absent' }
  try {
    return await invokeTauri<CaptionModelStatus>(
      'recording_caption_model_status',
    )
  } catch {
    return { supported: false, state: 'absent' }
  }
}

/** Downloads ~142MB once; progress via recording:caption-model-progress. */
export async function downloadCaptionModel(): Promise<void> {
  await invokeTauri('recording_caption_model_download')
}

/**
 * Preference + model readiness, resolved fresh at recording start. Every
 * startRecording() call site must pass this as `liveCaptions` — cached
 * component state goes stale across windows and app restarts.
 */
export async function resolveLiveCaptionsEnabled(): Promise<boolean> {
  return (
    isLiveCaptionsPreferred() &&
    (await getCaptionModelStatus()).state === 'ready'
  )
}

export async function updateRecordingTemplate(
  summaryTemplate: string | null,
): Promise<void> {
  await invokeTauri('recording_update_template', { summaryTemplate })
}

export async function pauseRecording(): Promise<void> {
  await invokeTauri('recording_pause')
}

export async function resumeRecording(): Promise<void> {
  await invokeTauri('recording_resume')
}

export async function stopRecording(): Promise<RecordingInfo> {
  return invokeTauri<RecordingInfo>('recording_stop')
}

export async function getRecordingState(): Promise<RecordingStateDto | null> {
  if (!isTauri()) return null
  try {
    return await invokeTauri<RecordingStateDto>('recording_get_state')
  } catch {
    return null
  }
}

export async function listLocalRecordings(): Promise<RecordingInfo[]> {
  if (!isTauri()) return []
  return invokeTauri<RecordingInfo[]>('recording_list_local')
}

export async function listMicDevices(): Promise<MicDevice[]> {
  if (!isTauri()) return []
  return invokeTauri<MicDevice[]>('recording_list_mic_devices')
}

export async function deleteLocalRecording(id: string): Promise<void> {
  await invokeTauri('recording_delete_local', { id })
}

/**
 * Upload a finished local recording. Auth is pulled from desktop secure
 * storage here so the Rust side stays auth-agnostic. The current org id is
 * required — the API resolves org context (incl. the meeting_notes feature
 * flag) from the x-organization-id header, and without it the request is
 * evaluated against the user's default org instead of the one they're in.
 */
export async function uploadRecording(id: string): Promise<void> {
  const tokens = await desktopSecureStorage.getTokens()
  if (!tokens?.accessToken) {
    throw new Error('Not signed in')
  }
  const organizationId =
    useOrganizationStore.getState().currentOrganization?.id ?? null
  await invokeTauri('recording_upload', {
    id,
    auth: {
      accessToken: tokens.accessToken,
      apiBaseUrl: env.apiUrl,
      organizationId,
    },
  })
}

// ===== Event listeners (return unlisten fns) =====

type Unlisten = () => void

async function listenTauri<T>(
  event: string,
  callback: (payload: T) => void,
): Promise<Unlisten> {
  if (!isTauri()) return () => {}
  const { listen } = await import('@tauri-apps/api/event')
  return listen<T>(event, e => callback(e.payload))
}

export function listenForRecordingState(
  callback: (payload: RecordingStatePayload) => void,
): Promise<Unlisten> {
  return listenTauri('recording:state', callback)
}

export function listenForRecordingLevel(
  callback: (payload: RecordingLevelPayload) => void,
): Promise<Unlisten> {
  return listenTauri('recording:level', callback)
}

export function listenForRecordingError(
  callback: (payload: RecordingErrorPayload) => void,
): Promise<Unlisten> {
  return listenTauri('recording:error', callback)
}

export function listenForRecordingUploadProgress(
  callback: (payload: RecordingUploadProgressPayload) => void,
): Promise<Unlisten> {
  return listenTauri('recording:upload-progress', callback)
}

export function listenForRecordingUploadComplete(
  callback: (payload: { recordingId: string; apiRecordingId: string }) => void,
): Promise<Unlisten> {
  return listenTauri('recording:upload-complete', callback)
}

export function listenForRecordingOrphans(
  callback: (payload: RecordingInfo[]) => void,
): Promise<Unlisten> {
  return listenTauri('recording:orphans-found', callback)
}

export function listenForRecordingAutoStopped(
  callback: (payload: { recordingId: string; limitMs: number }) => void,
): Promise<Unlisten> {
  return listenTauri('recording:auto-stopped', callback)
}

export function listenForRecordingCaption(
  callback: (payload: CaptionPayload) => void,
): Promise<Unlisten> {
  return listenTauri('recording:caption', callback)
}

export type CaptionStatusReason = 'starting' | 'model_absent' | 'error'

export interface CaptionStatusPayload {
  active: boolean
  /** 'starting' = requested, engine loading; others = won't run this session */
  reason?: CaptionStatusReason | string
}

export function listenForCaptionStatus(
  callback: (payload: CaptionStatusPayload) => void,
): Promise<Unlisten> {
  return listenTauri('recording:caption-status', callback)
}

export function listenForCaptionModelProgress(
  callback: (payload: { downloadedBytes: number; totalBytes: number }) => void,
): Promise<Unlisten> {
  return listenTauri('recording:caption-model-progress', callback)
}

export function listenForRecordingSystemAudio(
  callback: (payload: { captured: boolean; reason?: string }) => void,
): Promise<Unlisten> {
  return listenTauri('recording:system-audio', callback)
}
